/**
 * WEB-925 — override `getUserTickets` in this app so a 404 fulfills as `{ tickets: [] }`.
 *
 * Must run before any `useGetUserTicketsQuery` subscriber mounts. A fulfilled
 * empty payload never hits `rtkQueryErrorMiddleware`, so Ticket History does
 * not raise the "Connection Error" toast. Does not edit `@loci/api` source —
 * `injectEndpoints({ overrideExisting: true })` is the web-only companion to
 * the API Leadbot change (`200 + []`).
 */

import { lociApi } from '@loci/api';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { UserTicketsResponse } from '@loci/types';
import { resolveUserTicketsQueryResult } from './honestEmptyTickets';

let installed = false;

export function installHonestEmptyTicketsQuery(): void {
  if (installed) {
    return;
  }
  installed = true;

  lociApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getUserTickets: builder.query<
        UserTicketsResponse,
        { limit?: number; offset?: number; sort?: string }
      >({
        async queryFn(arg, _api, _extraOptions, baseQuery) {
          const { limit, offset, sort } = arg ?? {};
          const result = await baseQuery({
            url: 'v1/users/me/tickets',
            params: {
              ...(limit !== undefined ? { limit } : {}),
              ...(offset !== undefined ? { offset } : {}),
              ...(sort !== undefined ? { sort } : {}),
            },
          });
          return resolveUserTicketsQueryResult(
            {
              ...(result.data !== undefined ? { data: result.data } : {}),
              ...(result.error !== undefined ? { error: result.error as FetchBaseQueryError } : {}),
            },
            { limit, offset },
          );
        },
        providesTags: [{ type: 'Ticket', id: 'LIST' }],
      }),
    }),
  });
}
